import { useState } from "react";
import { TextInput } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import {
  THEME_COLOR_KEYS,
  THEME_SWATCHES,
  isHexColor,
  isLegible,
  normalizeHex,
  type ThemeColorKey,
  type ThemeColors,
} from "@/modules/manage/lib/themeColors";

/**
 * The colour editor — the one part of a restaurant theme a seller may change
 * beyond copy and section visibility.
 *
 * It is safe to offer precisely because the skins are token-relative: every one
 * of them derives its hairlines, washes and muted text with `color-mix` off
 * `--store-*`, so moving the primary moves the buttons, the prices, the
 * selected options and the check bar together rather than leaving a page half
 * repainted. Structure stays locked (D15) — nothing here can move a section.
 *
 * ## Two inputs per colour, on purpose
 *
 * The swatch is for choosing and the text field is for pasting. A restaurant
 * that has a brand already has its hex written down somewhere, and making them
 * hunt for it in a colour wheel is worse than a six-character field.
 *
 * ## No save button
 *
 * A colour is judged by looking at it, so every valid change goes straight to
 * the preview and the editor persists it on its own debounce. Undo is in the
 * top bar, and "Reset to the theme's design" puts the whole composition back.
 *
 * ## Contrast is warned, not enforced
 *
 * A pale primary makes the label on every button unreadable, and no CSS can
 * work that out — so it is computed here and said plainly. It is not blocked:
 * this is the restaurant's own brand, and refusing a colour a real shopfront
 * already uses would be the tool overruling the owner.
 */
export function ThemeColorsEditor({
  colors,
  onChange,
  disabled,
  compact = false,
}: {
  /** What the draft currently holds. */
  colors: ThemeColors;
  /**
   * Fired on every VALID change, immediately. There is no save button: the
   * editor repaints the preview from this and persists on its own debounce, so
   * a seller drags a colour and watches the storefront follow.
   */
  onChange: (next: ThemeColors) => void;
  disabled: boolean;
  /**
   * The editor's 290px rail. Two columns and a line of help text under each
   * label need roughly twice that: at rail width the hints wrap to four or five
   * words a line and the control becomes unreadable. Compact drops to one
   * column and lets the label carry the meaning on its own.
   */
  compact?: boolean;
}) {
  const t = useTranslate();
  const [draft, setDraft] = useState<ThemeColors>(colors);

  const LABELS: Record<ThemeColorKey, { label: string; hint: string }> = {
    primary: {
      label: t((m) => m.manage.storefrontColorPrimary),
      hint: t((m) => m.manage.storefrontColorPrimaryHint),
    },
    accent: {
      label: t((m) => m.manage.storefrontColorAccent),
      hint: t((m) => m.manage.storefrontColorAccentHint),
    },
    surface: {
      label: t((m) => m.manage.storefrontColorSurface),
      hint: t((m) => m.manage.storefrontColorSurfaceHint),
    },
    ink: {
      label: t((m) => m.manage.storefrontColorInk),
      hint: t((m) => m.manage.storefrontColorInkHint),
    },
  };

  /**
   * UX-round D60, slice 10.10 — the swatch names, looked up once.
   * «UX-round Dn» = the 2026-09-17 UX round's own numbering (`.roundtable/2026-09-17-restaurant-ux/decisions.md`), not §11's.
   *
   * A `Record` rather than a `switch` in the loop: the palette is data in
   * `themeColors.ts` and the words for it belong beside the rest of this
   * screen's words, so the only thing shared between them is the key.
   */
  const SWATCH_NAMES: Record<string, string> = {
    swatchNearBlack: t((m) => m.manage.swatchNearBlack),
    swatchGrey: t((m) => m.manage.swatchGrey),
    swatchWhite: t((m) => m.manage.swatchWhite),
    swatchCream: t((m) => m.manage.swatchCream),
    swatchDarkGreen: t((m) => m.manage.swatchDarkGreen),
    swatchGreen: t((m) => m.manage.swatchGreen),
    swatchRed: t((m) => m.manage.swatchRed),
    swatchOrange: t((m) => m.manage.swatchOrange),
    swatchYellow: t((m) => m.manage.swatchYellow),
    swatchBlue: t((m) => m.manage.swatchBlue),
    swatchPurple: t((m) => m.manage.swatchPurple),
    swatchBrown: t((m) => m.manage.swatchBrown),
  };

  /** Which key's exact-code row is open. One at a time; closed by default. */
  const [codeOpen, setCodeOpen] = useState<ThemeColorKey | null>(null);

  const set = (key: ThemeColorKey, value: string) => {
    const next = { ...draft, [key]: value };
    setDraft(next);
    /* Only a complete colour goes upstream. A half-typed hex is not a state
       the storefront should be asked to render. */
    if (THEME_COLOR_KEYS.every((k) => isHexColor(next[k]))) onChange(next);
  };

  const invalid = THEME_COLOR_KEYS.filter((key) => !isHexColor(draft[key]));
  /* Body copy on the page ground, and a button label on the primary — the two
     pairs a diner reads constantly. */
  const unreadable =
    invalid.length === 0 &&
    (!isLegible(draft.ink, draft.surface) ||
      (!isLegible("#ffffff", draft.primary) && !isLegible(draft.ink, draft.primary)));

  return (
    <div className="flex flex-col gap-4">
      <div className={compact ? "flex flex-col gap-2.5" : "grid gap-3 sm:grid-cols-2"}>
        {THEME_COLOR_KEYS.map((key) => {
          const value = draft[key];
          const valid = isHexColor(value);
          return (
            <div key={key} className="flex flex-col gap-2">
              <span className="min-w-0">
                <span className="block text-sm font-medium">
                  {LABELS[key].label}
                </span>
                {compact ? null : (
                  <span className="block text-xs text-[color:var(--muted-foreground)]">
                    {LABELS[key].hint}
                  </span>
                )}
              </span>

              {/*
                UX-round D60, slice 10.10 — SWATCHES FIRST.

                The owner's words, looking at four fields reading `#1c1c1c`,
                `#000000`, `#ffffff`, `#1c1c1c`: *"I do not know my hex codes. I
                know my sign is dark green."* So the first control is a row of
                named colours, and the exact code — the well and the hex field,
                both of which were already here and are right for a seller with
                a brand book — moves behind a disclosure.

                Each swatch is a real button with a name: `aria-label` carries
                the word, `aria-pressed` carries the state, and the tick is
                decorative. A grid of unlabelled coloured squares is not a
                control an owner can use over the phone.
              */}
              <div
                role="group"
                aria-label={LABELS[key].label}
                className="flex flex-wrap gap-1.5"
              >
                {THEME_SWATCHES.map((swatch) => {
                  const isPicked = value.toLowerCase() === swatch.hex;
                  return (
                    <button
                      key={swatch.hex}
                      type="button"
                      disabled={disabled}
                      aria-pressed={isPicked}
                      aria-label={SWATCH_NAMES[swatch.name] ?? swatch.hex}
                      title={SWATCH_NAMES[swatch.name] ?? swatch.hex}
                      onClick={() => set(key, swatch.hex)}
                      data-testid={`storefront-swatch-${key}-${swatch.name}`}
                      className={[
                        "size-9 shrink-0 rounded-[var(--radius-control)] border-2 transition-transform",
                        isPicked
                          ? "border-[color:var(--accent)] scale-110"
                          : "border-[color:var(--border)]",
                        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
                      ].join(" ")}
                      style={{ background: swatch.hex }}
                    />
                  );
                })}
              </div>

              <button
                type="button"
                onClick={() => setCodeOpen(codeOpen === key ? null : key)}
                aria-expanded={codeOpen === key}
                data-testid={`storefront-color-code-toggle-${key}`}
                className="self-start text-xs font-semibold text-[color:var(--accent)] underline"
              >
                {t((m) => m.manage.storefrontColorExact)}
              </button>

              {codeOpen === key ? (
                <label className="flex items-center gap-3">
                  {/*
                    The swatch is the input itself, not a preview beside one: a
                    colour control the seller cannot click is a picture of a
                    control. A hex the browser would reject falls back to the
                    theme's own value so the well never goes black and saves
                    that.
                  */}
                  {/* eslint-disable-next-line no-restricted-syntax -- there is
                      no colour-well primitive in @a2z/ui, and this is its only
                      use in the product; a wrapper would be a primitive with
                      one caller. */}
                  <input
                    type="color"
                    value={valid ? value : colors[key]}
                    onChange={(event) => set(key, normalizeHex(event.target.value))}
                    disabled={disabled}
                    aria-label={LABELS[key].label}
                    className="size-11 shrink-0 cursor-pointer rounded-[var(--radius-control)] border border-[color:var(--border)] bg-transparent p-1"
                  />
                  <span className="w-[7.5rem] shrink-0">
                    <TextInput
                      name={`color-${key}`}
                      value={value}
                      onChange={(event) => set(key, event.target.value)}
                      onBlur={(event) => set(key, normalizeHex(event.target.value))}
                      disabled={disabled}
                      aria-label={LABELS[key].label}
                      aria-invalid={!valid}
                      data-testid={`storefront-color-${key}`}
                      /* The code reads left-to-right in BOTH locales — a hex is
                         not language, and mirroring it makes it unreadable. */
                      dir="ltr"
                    />
                  </span>
                </label>
              ) : null}
            </div>
          );
        })}
      </div>

      {invalid.length > 0 ? (
        <p className="text-sm" style={{ color: "var(--destructive, #b4443c)" }}>
          {t((m) => m.manage.storefrontColorsInvalid)}
        </p>
      ) : unreadable ? (
        <p className="text-sm text-[color:var(--muted-foreground)]">
          {t((m) => m.manage.storefrontColorsContrast)}
        </p>
      ) : null}

    </div>
  );
}
